/**
 * Test Data Charts utility module.
 *
 * TypeScript rewrite of the legacy jQuery + jQPlot implementation.
 * Fetches test data series via the project Axios instance and renders
 * SVG line/bar charts and data tables into designated DOM elements.
 *
 * All user-supplied strings are HTML-escaped before DOM insertion to prevent XSS.
 * Tables are built with createElement; charts are injected via DOMParser to avoid innerHTML.
 */

import api from '../api/axios';
import endpoints from '../api/endpoints';

// ─── Domain types ─────────────────────────────────────────────────────────────

export interface TdsItem {
  tocId: string;
  testOfChange: string;
  tdsId: string;
  /** Date in "M-D-YYYY" format, e.g. "4-18-2026" */
  testDate: string;
  shiftId: string;
  shiftDesc: string;
  outsideVarValue: string | null;
  numeratorValue: number;
  denominatorValue: number;
  value: number;
  notes: string | null;
}

export interface TocItem {
  testOfChange: string;
  outcome: string;
  rating: number;
  ratingDesc: string;
  testOfChangeTitle: string;
}

export interface TdsOutVarItem {
  outsideVarValue: string | null;
  sumNumerator: number;
  sumDenominator: number;
}

export interface ChartData {
  tdsList: TdsItem[];
  tdsOutVarList: TdsOutVarItem[];
  tocList: TocItem[];
}

interface ChartApiResponse {
  data: ChartData;
}

export interface ChartParams {
  teamName: string;
  pipName: string;
  numeratorName: string;
  denominatorName: string;
  valueName: string;
  outsideVarName: string;
}

// ─── SVG layout constants ──────────────────────────────────────────────────────

const SVG_LINE_W = 700;
const SVG_LINE_H = 280;
const SVG_LINE_PAD_L = 65;
const SVG_LINE_PAD_R = 15;
const SVG_LINE_PAD_T = 15;
const SVG_LINE_PAD_B = 70;
const PLOT_W = SVG_LINE_W - SVG_LINE_PAD_L - SVG_LINE_PAD_R;
const PLOT_H = SVG_LINE_H - SVG_LINE_PAD_T - SVG_LINE_PAD_B;

const SVG_BAR_W = 500;
const SVG_BAR_H = 300;
const SVG_BAR_PAD_L = 65;
const SVG_BAR_PAD_R = 15;
const SVG_BAR_PAD_T = 30;
const SVG_BAR_PAD_B = 100;
const BAR_PLOT_W = SVG_BAR_W - SVG_BAR_PAD_L - SVG_BAR_PAD_R;
const BAR_PLOT_H = SVG_BAR_H - SVG_BAR_PAD_T - SVG_BAR_PAD_B;

const SERIES_COLORS = [
  '#0070B9', '#E8560A', '#2ECC71', '#9B59B6',
  '#F39C12', '#1ABC9C', '#E74C3C', '#3498DB',
];

const Y_TICK_COUNT = 7;

// ─── Pure utility functions ────────────────────────────────────────────────────

/**
 * Rounds a value to 4 decimal places for display, matching the legacy
 * `formatValueForDisplay` behaviour.
 */
export const formatValueForDisplay = (value: number): number =>
  Math.round(value * 10000) / 10000;

/**
 * Computes the median of a numeric array.
 * Returns 0 for an empty array.
 */
export const getMedian = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Decodes a URI-encoded project name, replacing the legacy `&dq;` token with
 * a double-quote and applying URI decoding.
 */
export const cleanForViewing = (text: string): string => {
  try {
    return decodeURIComponent(text.replaceAll('&dq;', '"'));
  } catch {
    return text.replaceAll('&dq;', '"');
  }
};

/**
 * HTML-escapes a string to prevent XSS when inserting into SVG/HTML.
 * Uses the browser's own text-node escaping via textContent assignment.
 */
const escapeHtml = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const el = document.createElement('span');
  el.textContent = value;
  return el.innerHTML;
};

/**
 * Truncates a string to maxLength, appending '...' when shortened.
 */
const truncateText = (text: string, maxLength: number): string =>
  text.length > maxLength ? `${text.substring(0, maxLength - 2)}...` : text;

/**
 * Parses a date string into a UTC millisecond timestamp.
 * Supports: "M-D-YYYY", "YYYY-MM-DD" (ISO), "M/D/YYYY" (slash).
 * Uses Date.UTC so all timestamps are timezone-independent; the 86_400_000 ms/day
 * arithmetic in tick generation then works correctly in any locale.
 */
const parseTestDate = (dateStr: string): number => {
  // ISO format: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const p = dateStr.split('-');
    return Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  }
  // Slash format: M/D/YYYY or MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const p = dateStr.split('/');
    return Date.UTC(Number(p[2]), Number(p[0]) - 1, Number(p[1]));
  }
  // Default: M-D-YYYY
  const parts = dateStr.split('-');
  if (parts.length !== 3) return 0;
  return Date.UTC(Number.parseInt(parts[2], 10), Number.parseInt(parts[0], 10) - 1, Number.parseInt(parts[1], 10));
};

// ─── DOM helpers ───────────────────────────────────────────────────────────────

const getElementById = (id: string): HTMLElement | null =>
  document.getElementById(id);

const setVisible = (el: HTMLElement, visible: boolean): void => {
  el.style.display = visible ? 'block' : 'none';
};

/**
 * Parses an SVG string and inserts the resulting node into a container element.
 * Uses DOMParser with 'image/svg+xml' to avoid innerHTML XSS risks.
 */
const injectSvg = (container: HTMLElement, svgMarkup: string): void => {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgMarkup, 'image/svg+xml');
  const svgNode = document.adoptNode(svgDoc.documentElement);
  container.replaceChildren(svgNode);
};

const createTh = (text: string, width?: string): HTMLTableCellElement => {
  const th = document.createElement('th');
  th.className = 'data';
  th.textContent = text;
  if (width) th.style.width = width;
  return th;
};

const createTd = (text: string, alignRight = false): HTMLTableCellElement => {
  const td = document.createElement('td');
  td.className = 'data';
  td.textContent = text;
  if (alignRight) td.style.textAlign = 'right';
  return td;
};

// ─── Line chart SVG builders ───────────────────────────────────────────────────

/** Groups TdsItem list into per-series maps keyed by tocId_shiftId. */
const groupIntoSeries = (tdsList: TdsItem[]): Map<string, TdsItem[]> => {
  const map = new Map<string, TdsItem[]>();
  for (const item of tdsList) {
    const key = `${item.tocId}_${item.shiftId}`;
    if (!map.has(key)) map.set(key, []);
    const series = map.get(key);
    if (series) series.push(item);
  }
  return map;
};

/**
 * Computes time-proportional x position for a date.
 * Matches jQPlot DateAxisRenderer: gaps are proportional to elapsed calendar time.
 */
const xPosTime = (dateStr: string, minTime: number, timeRange: number): number => {
  if (timeRange === 0) return PLOT_W / 2;
  return ((parseTestDate(dateStr) - minTime) / timeRange) * PLOT_W;
};

/** Computes y position for a value within the y-axis range. */
const yPos = (value: number, yMin: number, yRange: number): number =>
  PLOT_H - ((value - yMin) / yRange) * PLOT_H;

/** Builds y-axis tick mark and grid line SVG markup. */
const buildYAxisTicks = (yMin: number, yRange: number): string => {
  let markup = '';
  for (let i = 0; i < Y_TICK_COUNT; i++) {
    const val = yMin + (yRange * i) / (Y_TICK_COUNT - 1);
    const y = PLOT_H - ((val - yMin) / yRange) * PLOT_H;
    const label = escapeHtml(String(formatValueForDisplay(val)));
    markup += `<g transform="translate(0,${y})">`;
    markup += `<line x1="-5" y1="0" x2="${PLOT_W}" y2="0" stroke="#cccccc" stroke-width="1"/>`;
    markup += `<text x="-8" y="4" text-anchor="end" font-size="10" fill="#6b7280">${label}</text>`;
    markup += '</g>';
  }
  return markup;
};

/**
 * Builds continuous date x-axis ticks with vertical grid lines for the line chart.
 * Generates evenly-spaced ticks across the full plot range (floor-aligned to tick
 * boundaries so an extra tick appears before the first data point, matching jQPlot
 * DateAxisRenderer).  Each tick has a full-height vertical grid line.
 */
const buildLineXAxisTicks = (plotMinTime: number, plotMaxTime: number, plotTimeRange: number): string => {
  const rangeDays = (plotMaxTime - plotMinTime) / 86_400_000;
  let stepDays: number;
  if (rangeDays <= 7)        stepDays = 1;
  else if (rangeDays <= 14)  stepDays = 2;
  else if (rangeDays <= 45)  stepDays = 4;
  else if (rangeDays <= 90)  stepDays = 7;
  else if (rangeDays <= 180) stepDays = 14;
  else                       stepDays = 30;

  const stepMs = stepDays * 86_400_000;
  // floor so first tick is at or before the plot start (matches jQPlot axis extension)
  const start = Math.floor(plotMinTime / stepMs) * stepMs;
  const ticks: string[] = [];
  for (let t = start; t <= plotMaxTime; t += stepMs) {
    const d = new Date(t);
    // Use UTC methods — consistent with Date.UTC-based parseTestDate
    ticks.push(`${d.getUTCMonth() + 1}-${d.getUTCDate()}-${d.getUTCFullYear()}`);
  }

  return ticks
    .map((d) => {
      const x = xPosTime(d, plotMinTime, plotTimeRange);
      // Skip ticks that fall outside the visible plot area to avoid lines behind the y-axis
      if (x < 0 || x > PLOT_W) return '';
      return (
        `<g transform="translate(${x},${PLOT_H})">` +
        // Full-height vertical grid line + tick stub
        `<line x1="0" y1="${-PLOT_H}" x2="0" y2="5" stroke="#cccccc" stroke-width="1"/>` +
        `<text transform="rotate(-40)" x="-4" y="14" text-anchor="end" font-size="9" fill="#6b7280">${escapeHtml(d)}</text>` +
        `</g>`
      );
    })
    .join('');
};

interface SeriesResult {
  markup: string;
  legendItems: Array<{ label: string; color: string }>;
}

/** Builds polyline and dot SVG markup for every data series. */
const buildSeriesMarkup = (
  seriesMap: Map<string, TdsItem[]>,
  minTime: number,
  timeRange: number,
  yMin: number,
  yRange: number,
): SeriesResult => {
  const legendItems: Array<{ label: string; color: string }> = [];
  let markup = '';
  let idx = 0;

  for (const [, items] of seriesMap) {
    const color = SERIES_COLORS[idx % SERIES_COLORS.length];
    const sorted = [...items].sort((a, b) => parseTestDate(a.testDate) - parseTestDate(b.testDate));
    const label = truncateText(`${sorted[0].testOfChange}-${sorted[0].shiftDesc}`, 20);
    const points = sorted
      .map((item) => `${xPosTime(item.testDate, minTime, timeRange)},${yPos(item.value, yMin, yRange)}`)
      .join(' ');

    markup += `<polyline points="${points}" fill="none" stroke="${color}" stroke-width="2"/>`;

    for (const item of sorted) {
      const cx = xPosTime(item.testDate, minTime, timeRange);
      const cy = yPos(item.value, yMin, yRange);
      // data-tip drives the custom cursor-following tooltip (matches jQPlot highlighter format)
      const tip = escapeHtml(`${item.testDate}, ${formatValueForDisplay(item.value)}`);
      markup += `<circle cx="${cx}" cy="${cy}" r="4" fill="${color}" data-tip="${tip}" style="cursor:pointer"/>`;    }

    legendItems.push({ label, color });
    idx++;
  }

  return { markup, legendItems };
};

/** Builds chart legend SVG markup positioned below the plot area. */
const buildLegendMarkup = (
  legendItems: Array<{ label: string; color: string }>,
): string => {
  let markup = legendItems
    .map(
      ({ label, color }, i) =>
        `<g transform="translate(${SVG_LINE_PAD_L},${SVG_LINE_H + 5 + i * 20})">` +
        `<line x1="0" y1="8" x2="18" y2="8" stroke="${color}" stroke-width="2"/>` +
        `<circle cx="9" cy="8" r="3" fill="${color}"/>` +
        `<text x="24" y="13" font-size="11" fill="#374151">${escapeHtml(label)}</text>` +
        `</g>`,
    )
    .join('');

  const medianLabel = escapeHtml(`Median`);
  markup +=
    `<g transform="translate(${SVG_LINE_PAD_L},${SVG_LINE_H + 5 + legendItems.length * 20})">` +
    `<line x1="0" y1="8" x2="18" y2="8" stroke="rgb(194,181,135)" stroke-width="1.5"/>` +
    `<text x="24" y="13" font-size="11" fill="#374151">${medianLabel}</text>` +
    `</g>`;

  return markup;
};

/**
 * Generates a complete SVG line chart string from test data series.
 * Groups entries by tocId+shiftId and adds a horizontal median reference line.
 * X-axis: continuous date ticks with vertical grid lines (jQPlot DateAxisRenderer).
 * Y-axis: 20% padding + 7 ticks (jQPlot autoscale — matches legacy 1.4→5.6 step 0.7).
 */
export const generateLineChartSvg = (tdsList: TdsItem[], valueName: string): string => {
  const seriesMap = groupIntoSeries(tdsList);

  const allTimes = tdsList.map((d) => parseTestDate(d.testDate));
  const minTime = Math.min(...allTimes);
  const maxTime = Math.max(...allTimes);
  const dataTimeRange = maxTime - minTime;

  // Extend the plot range by 5% on each side so there is whitespace before the
  // first and after the last data point, matching jQPlot DateAxisRenderer.
  // For single-point data (dataTimeRange = 0) fall back to ±7 days so the axis
  // has a meaningful range and several tick marks are generated.
  const timePad = dataTimeRange * 0.05 || 7 * 86_400_000;
  const plotMinTime = minTime - timePad;
  const plotMaxTime = maxTime + timePad;
  const plotTimeRange = plotMaxTime - plotMinTime;

  const dateSet = new Set<string>(tdsList.map((d) => d.testDate));
  const dates = Array.from(dateSet).sort((a, b) => parseTestDate(a) - parseTestDate(b));

  const allValues = tdsList.map((d) => d.value);
  const median = getMedian(allValues);
  const rawMin = Math.min(...allValues, median);
  const rawMax = Math.max(...allValues, median);
  // 20% padding on each side matches jQPlot autoscale (legacy: data 2–5 → axis 1.4–5.6).
  // For uniform data (all same value) fall back to 50% of the value so the axis is
  // still meaningful (e.g. value=0.185 → pad=0.0925).
  const pad = (rawMax - rawMin) * 0.2 || Math.max(rawMax * 0.5, 0.1);
  // Allow the y-axis to extend below zero when padding pushes the lower bound
  // into negative territory — this matches legacy jQPlot autoscale behaviour and
  // prevents small-decimal datasets from being compressed against the X-axis.
  const yMin = rawMin - pad;
  const yRange = rawMax + pad - yMin || 1;

  // Median line from first to last data date, positioned using extended plot range
  const firstX = xPosTime(dates[0], plotMinTime, plotTimeRange);
  const lastDateIndex = dates.length - 1;
  const lastX  = xPosTime(dates[lastDateIndex], plotMinTime, plotTimeRange);
  const medianY = yPos(median, yMin, yRange);
  const medianLine =
    `<line x1="${firstX}" y1="${medianY}" x2="${lastX}" y2="${medianY}" stroke="rgb(121, 112, 80)" stroke-width="1.5"/>` +
    `<circle cx="${firstX}" cy="${medianY}" r="4" fill="rgb(121, 112, 80)"/>` +
    `<circle cx="${lastX}" cy="${medianY}" r="4" fill="rgb(121, 112, 80)"/>`;

  const { markup: seriesMarkup, legendItems } = buildSeriesMarkup(seriesMap, plotMinTime, plotTimeRange, yMin, yRange);
  const totalH = SVG_LINE_H + (legendItems.length + 1) * 20 + 15;
  const ariaLabel = escapeHtml(`${valueName} - Line Chart`);
  const yLabel = escapeHtml(valueName);

  return (
    `<svg viewBox="0 0 ${SVG_LINE_W} ${totalH}" width="100%" role="img" aria-label="${ariaLabel}">` +
    `<title>${ariaLabel}</title>` +
    `<g transform="translate(${SVG_LINE_PAD_L},${SVG_LINE_PAD_T})">` +
    buildYAxisTicks(yMin, yRange) +
    `<line x1="0" y1="0" x2="0" y2="${PLOT_H}" stroke="#6b7280" stroke-width="1.5"/>` +
    `<line x1="0" y1="${PLOT_H}" x2="${PLOT_W}" y2="${PLOT_H}" stroke="#6b7280" stroke-width="1.5"/>` +
    buildLineXAxisTicks(plotMinTime, plotMaxTime, plotTimeRange) +
    `<text transform="translate(${-SVG_LINE_PAD_L + 12},${PLOT_H / 2}) rotate(-90)" ` +
    `text-anchor="middle" font-size="11" fill="#374151">${yLabel}</text>` +
    medianLine +
    seriesMarkup +
    `</g>` +
    buildLegendMarkup(legendItems) +
    // Tooltip overlay — rendered last so it sits on top of all other elements
    `<g class="chart-tooltip" visibility="hidden">` +
    `<rect rx="4" ry="4" fill="#1e3a5f" opacity="0.88"/>` +
    `<text fill="#fff" font-size="11"/>` +
    `</g>` +
    `</svg>`
  );
};

// ─── Bar chart SVG builders ────────────────────────────────────────────────────

interface BarEntry {
  label: string;
  value: number;
}

/** Computes bar data (label + value) from tdsOutVarList. */
const computeBarEntries = (tdsOutVarList: TdsOutVarItem[]): BarEntry[] =>
  tdsOutVarList.map((item) => ({
    label: item.outsideVarValue === null || item.outsideVarValue === ''
      ? '--'
      : item.outsideVarValue,
    value: item.sumDenominator === 0
      ? 0
      : formatValueForDisplay(item.sumNumerator / item.sumDenominator),
  }));

/** Builds y-axis tick SVG markup for the bar chart. */
const buildBarYAxisTicks = (maxVal: number): string => {
  let markup = '';
  for (let i = 0; i < Y_TICK_COUNT; i++) {
    const val = (maxVal * i) / (Y_TICK_COUNT - 1);
    const y = BAR_PLOT_H - (val / maxVal) * BAR_PLOT_H;
    const label = escapeHtml(String(formatValueForDisplay(val)));
    markup += `<g transform="translate(0,${y})">`;
    markup += `<line x1="-5" y1="0" x2="${BAR_PLOT_W}" y2="0" stroke="#cccccc" stroke-width="1"/>`;
    markup += `<text x="-8" y="4" text-anchor="end" font-size="10" fill="#6b7280">${label}</text>`;
    markup += '</g>';
  }
  return markup;
};

/** Returns true if a string matches any supported date format (M-D-YYYY, YYYY-MM-DD, M/D/YYYY). */
const isDateString = (s: string): boolean =>
  /^\d{1,2}-\d{1,2}-\d{4}$/.test(s) ||
  /^\d{4}-\d{2}-\d{2}$/.test(s) ||
  /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s);

/** Computes time-proportional x position within BAR_PLOT_W. */
const xPosTimeBar = (dateStr: string, minTime: number, timeRange: number): number => {
  if (timeRange === 0) return BAR_PLOT_W / 2;
  return ((parseTestDate(dateStr) - minTime) / timeRange) * BAR_PLOT_W;
};

/**
 * Generates "nice" evenly-spaced date tick strings spanning [minTime, maxTime].
 * Interval is chosen to produce roughly 5-8 ticks based on range size.
 */
const niceDateTicks = (minTime: number, maxTime: number): string[] => {
  const rangeDays = (maxTime - minTime) / 86_400_000;
  let stepDays: number;
  if (rangeDays <= 7)        stepDays = 1;
  else if (rangeDays <= 14)  stepDays = 2;
  else if (rangeDays <= 45)  stepDays = 4;
  else if (rangeDays <= 90)  stepDays = 7;
  else if (rangeDays <= 180) stepDays = 14;
  else                       stepDays = 30;

  const stepMs = stepDays * 86_400_000;
  const start = Math.ceil(minTime / stepMs) * stepMs;
  const ticks: string[] = [];
  for (let t = start; t <= maxTime; t += stepMs) {
    const d = new Date(t);
    // Use UTC methods — timestamps are UTC-based (Date.UTC in parseTestDate)
    ticks.push(`${d.getUTCMonth() + 1}-${d.getUTCDate()}-${d.getUTCFullYear()}`);
  }
  // Ensure at least one tick is always visible
  if (ticks.length === 0) {
    const d = new Date(minTime);
    ticks.push(`${d.getUTCMonth() + 1}-${d.getUTCDate()}-${d.getUTCFullYear()}`);
  }
  return ticks;
};

/**
 * Builds a continuous date x-axis with vertical grid lines for the bar chart.
 * Matches jQPlot DateAxisRenderer: the full date range is shown with evenly-spaced
 * ticks even when only a few dates have data bars.
 */
const buildBarXAxisDateTicks = (minTime: number, maxTime: number, timeRange: number): string =>
  niceDateTicks(minTime, maxTime)
    .map((d) => {
      const x = xPosTimeBar(d, minTime, timeRange);
      return (
        `<g transform="translate(${x},0)">` +
        `<line x1="0" y1="0" x2="0" y2="${BAR_PLOT_H}" stroke="#cccccc" stroke-width="1"/>` +
        `<g transform="translate(0,${BAR_PLOT_H})">` +
        `<line x1="0" y1="0" x2="0" y2="5" stroke="#6b7280" stroke-width="1"/>` +
        `<text transform="rotate(-40)" x="-4" y="14" text-anchor="end" font-size="9" fill="#6b7280">${escapeHtml(d)}</text>` +
        `</g>` +
        `</g>`
      );
    })
    .join('');

/** Builds categorical x-axis ticks with vertical grid lines for the bar chart. */
const buildBarXAxisCategoryTicks = (bars: BarEntry[]): string => {
  const spacing = BAR_PLOT_W / Math.max(bars.length, 1);
  return bars
    .map(({ label }, i) => {
      const cx = spacing * i + spacing / 2;
      const tickLabel = escapeHtml(label.length > 12 ? `${label.substring(0, 12)}…` : label);
      return (
        `<g transform="translate(${cx},0)">` +
        `<line x1="0" y1="0" x2="0" y2="${BAR_PLOT_H}" stroke="#cccccc" stroke-width="1"/>` +
        `<g transform="translate(0,${BAR_PLOT_H})">` +
        `<line x1="0" y1="0" x2="0" y2="5" stroke="#6b7280" stroke-width="1"/>` +
        `<text transform="rotate(-45)" x="-4" y="14" text-anchor="end" font-size="9" fill="#6b7280">${tickLabel}</text>` +
        `</g>` +
        `</g>`
      );
    })
    .join('');
};

/**
 * Builds bar rect SVG markup.
 * When minTime and timeRange are provided, bars are positioned time-proportionally
 * (matching jQPlot DateAxisRenderer).  Otherwise equal categorical spacing is used.
 */
const buildBarsMarkup = (
  bars: BarEntry[],
  maxVal: number,
  minTime?: number,
  timeRange?: number,
): string => {
  const useDate = minTime !== undefined && timeRange !== undefined;
  const spacing = BAR_PLOT_W / Math.max(bars.length, 1);
  const barWidth = useDate
    ? Math.max(6, Math.min(30, spacing * 0.5))
    : Math.min(60, spacing * 0.6);

  return bars
    .map(({ label, value }, i) => {
      const cx = minTime !== undefined && timeRange !== undefined
        ? xPosTimeBar(label, minTime, timeRange)
        : spacing * i + spacing / 2;
      const bh = Math.max((value / maxVal) * BAR_PLOT_H, 0);
      const by = BAR_PLOT_H - bh;
      const color = SERIES_COLORS[i % SERIES_COLORS.length];
      // Tooltip format matches legacy jQPlot highlighter: "value: X"
      const tip = escapeHtml(`value: ${formatValueForDisplay(value)}`);
      return (
        `<rect x="${cx - barWidth / 2}" y="${by}" width="${barWidth}" height="${bh}" fill="${color}"` +
        ` data-tip="${tip}" style="cursor:pointer"/>`
      );
    })
    .join('');
};

/**
 * Generates a complete SVG bar chart string from outside variable summary data.
 * When outsideVarValue entries are date strings ("M-D-YYYY"), the x-axis renders
 * as a continuous date range with evenly-spaced ticks and vertical grid lines,
 * matching jQPlot DateAxisRenderer behaviour. Otherwise equal categorical spacing
 * is used.
 */
export const generateBarChartSvg = (
  tdsOutVarList: TdsOutVarItem[],
  valueName: string,
  outsideVarName: string,
): string => {
  const bars = computeBarEntries(tdsOutVarList);
  const maxVal = Math.max(...bars.map((b) => b.value), 0) * 1.15 || 1;
  const title = escapeHtml(`${valueName} by ${outsideVarName}`);
  const yLabel = escapeHtml(valueName);

  // If every non-placeholder label looks like a date, use continuous date axis.
  const dateBars = bars.filter((b) => b.label !== '--');
  const areDates = dateBars.length > 0 && dateBars.every((b) => isDateString(b.label));

  let xAxisMarkup: string;
  let barsMarkup: string;

  if (areDates) {
    const allTimes = dateBars.map((b) => parseTestDate(b.label));
    const minTime = Math.min(...allTimes);
    const maxTime = Math.max(...allTimes);
    const timeRange = maxTime - minTime || 1;
    xAxisMarkup = buildBarXAxisDateTicks(minTime, maxTime, timeRange);
    barsMarkup = buildBarsMarkup(bars, maxVal, minTime, timeRange);
  } else {
    xAxisMarkup = buildBarXAxisCategoryTicks(bars);
    barsMarkup = buildBarsMarkup(bars, maxVal);
  }

  return (
    `<svg viewBox="0 0 ${SVG_BAR_W} ${SVG_BAR_H}" width="100%" role="img" aria-label="${title}">` +
    `<title>${title}</title>` +
    `<text x="${SVG_BAR_W / 2}" y="20" text-anchor="middle" font-size="12" fill="#374151" font-weight="600">${title}</text>` +
    `<g transform="translate(${SVG_BAR_PAD_L},${SVG_BAR_PAD_T})">` +
    buildBarYAxisTicks(maxVal) +
    xAxisMarkup +
    `<line x1="0" y1="0" x2="0" y2="${BAR_PLOT_H}" stroke="#6b7280" stroke-width="1.5"/>` +
    `<line x1="0" y1="${BAR_PLOT_H}" x2="${BAR_PLOT_W}" y2="${BAR_PLOT_H}" stroke="#6b7280" stroke-width="1.5"/>` +
    `<text transform="translate(${-SVG_BAR_PAD_L + 12},${BAR_PLOT_H / 2}) rotate(-90)" ` +
    `text-anchor="middle" font-size="11" fill="#374151">${yLabel}</text>` +
    barsMarkup +
    `</g>` +
    // Tooltip overlay — rendered last so it sits on top of all other elements
    `<g class="chart-tooltip" visibility="hidden">` +
    `<rect rx="4" ry="4" fill="#1e3a5f" opacity="0.88"/>` +
    `<text fill="#fff" font-size="11"/>` +
    `</g>` +
    `</svg>`
  );
};

// ─── DOM rendering functions ───────────────────────────────────────────────────

/** Builds the team/project info table as a DOM node. */
const buildProjectInfoTable = (teamName: string, pipName: string): HTMLTableElement => {
  const table = document.createElement('table');
  table.className = 'data';
  const tbody = document.createElement('tbody');

  const addInfoRow = (label: string, value: string): void => {
    const row = document.createElement('tr');
    const th = document.createElement('th');
    th.className = 'teamdata';
    th.style.width = '120px';
    th.textContent = label;
    const td = document.createElement('td');
    td.className = 'teamdata';
    td.style.background = '#FCF5D3';
    td.textContent = value;
    row.appendChild(th);
    row.appendChild(td);
    tbody.appendChild(row);
  };

  addInfoRow('Name Of UBT:', teamName);
  addInfoRow('Name Of Project:', cleanForViewing(pipName));
  table.appendChild(tbody);
  return table;
};

/** Builds the Test of Change summary table as a DOM node. */
const buildTocTable = (tocList: TocItem[]): HTMLTableElement => {
  const table = document.createElement('table');
  table.className = 'data';

  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.appendChild(createTh('Test Of Change', '40%'));
  headerRow.appendChild(createTh('Outcome', '45%'));
  headerRow.appendChild(createTh('Rating', '15%'));
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const toc of tocList) {
    const row = document.createElement('tr');
    row.appendChild(createTd(toc.testOfChangeTitle));
    row.appendChild(createTd(toc.outcome));
    row.appendChild(createTd(`${toc.rating}-${toc.ratingDesc}`));
    tbody.appendChild(row);
  }
  table.appendChild(tbody);
  return table;
};

/**
 * Renders the project header and Test of Change summary table
 * into the #projectinfo and #tocinfo DOM elements.
 */
export const generateHeader = (teamName: string, pipName: string, tocList: TocItem[]): void => {
  const projectInfoEl = getElementById('projectinfo');
  const tocInfoEl = getElementById('tocinfo');

  if (projectInfoEl) {
    projectInfoEl.replaceChildren(buildProjectInfoTable(teamName, pipName));
    setVisible(projectInfoEl, true);
  }
  if (tocInfoEl) {
    tocInfoEl.replaceChildren(buildTocTable(tocList));
    setVisible(tocInfoEl, true);
  }
};

/** Builds the test data series table as a DOM node. */
const buildTestDataTable = (
  tdsList: TdsItem[],
  numeratorName: string,
  denominatorName: string,
  valueName: string,
  outsideVarName: string,
): HTMLTableElement => {
  const table = document.createElement('table');
  table.className = 'data';

  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  const headers: Array<[string, string]> = [
    ['Test Of Change', '16%'],
    ['Date Of Test', '14%'],
    ['Shift', '8%'],
    [numeratorName || 'Numerator', '12%'],
    [denominatorName || 'Denominator', '10%'],
    [valueName || 'Value', '10%'],
    ['Notes', '15%'],
    [outsideVarName || 'Other', '15%'],
  ];
  for (const [text, width] of headers) {
    headerRow.appendChild(createTh(text, width));
  }
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const item of tdsList) {
    const row = document.createElement('tr');
    row.appendChild(createTd(truncateText(item.testOfChange, 20)));
    row.appendChild(createTd(item.testDate));
    row.appendChild(createTd(item.shiftDesc));
    row.appendChild(createTd(String(item.numeratorValue)));
    row.appendChild(createTd(String(item.denominatorValue)));
    row.appendChild(createTd(String(formatValueForDisplay(item.value)), true));
    row.appendChild(createTd(item.notes ?? ''));
    row.appendChild(createTd(item.outsideVarValue ?? ''));
    tbody.appendChild(row);
  }
  table.appendChild(tbody);
  return table;
};

/**
 * Renders the test data series table into the #toctdsdatatable DOM element.
 */
export const generateTable = (
  tdsList: TdsItem[],
  numeratorName: string,
  denominatorName: string,
  valueName: string,
  outsideVarName: string,
): void => {
  const container = getElementById('toctdsdatatable');
  if (!container) return;
  container.replaceChildren(
    buildTestDataTable(tdsList, numeratorName, denominatorName, valueName, outsideVarName),
  );
  setVisible(container, true);
};

/**
 * Renders the SVG line chart into the #testofchangechartcomb DOM element.
 */
export const generateLineChartWithSvg = (tdsList: TdsItem[], valueName: string): void => {
  const container = getElementById('testofchangechartcomb');
  if (!container || tdsList.length === 0) return;
  injectSvg(container, generateLineChartSvg(tdsList, valueName));
};

/**
 * Renders the SVG bar chart into the #outsidevarchart DOM element.
 */
export const generateBarChartWithSvg = (
  tdsOutVarList: TdsOutVarItem[],
  valueName: string,
  outsideVarName: string,
): void => {
  const container = getElementById('outsidevarchart');
  if (!container || tdsOutVarList.length === 0) return;
  injectSvg(container, generateBarChartSvg(tdsOutVarList, valueName, outsideVarName));
};

/** Builds the outside variable summary table as a DOM node. */
const buildOutVarTable = (
  tdsOutVarList: TdsOutVarItem[],
  numeratorName: string,
  denominatorName: string,
  outsideVarName: string,
): HTMLTableElement => {
  const table = document.createElement('table');
  table.className = 'data';

  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.appendChild(createTh(outsideVarName || 'Other'));
  headerRow.appendChild(createTh(`Total ${numeratorName || 'Numerator'}`));
  headerRow.appendChild(createTh(`Total ${denominatorName || 'Denominator'}`));
  headerRow.appendChild(createTh('Total Average'));
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const item of tdsOutVarList) {
    const sumValue =
      item.sumDenominator === 0
        ? '0'
        : String(formatValueForDisplay(item.sumNumerator / item.sumDenominator));
    const row = document.createElement('tr');
    row.appendChild(createTd(item.outsideVarValue ?? ''));
    row.appendChild(createTd(String(item.sumNumerator)));
    row.appendChild(createTd(String(item.sumDenominator)));
    row.appendChild(createTd(sumValue, true));
    tbody.appendChild(row);
  }
  table.appendChild(tbody);
  return table;
};

/**
 * Renders the outside variable summary table into the #outvardatatable DOM element.
 */
export const generateBarChartTable = (
  tdsOutVarList: TdsOutVarItem[],
  numeratorName: string,
  denominatorName: string,
  _valueName: string,
  outsideVarName: string,
): void => {
  const container = getElementById('outvardatatable');
  if (!container) return;
  container.replaceChildren(buildOutVarTable(tdsOutVarList, numeratorName, denominatorName, outsideVarName));
  setVisible(container, true);
};

// ─── Page builder ──────────────────────────────────────────────────────────────

/**
 * Orchestrates full page rendering from a ChartData payload and display parameters.
 * Matches the structure of the legacy `buildPage` function.
 */
export const buildPage = (chartData: ChartData, params: ChartParams): void => {
  const { teamName, pipName, numeratorName, denominatorName, valueName, outsideVarName } = params;
  const { tdsList, tdsOutVarList, tocList } = chartData;

  const printBtn = getElementById('printButton');
  const messageEl = getElementById('message');

  if (tdsList.length === 0) {
    if (messageEl) {
      const msgDiv = document.createElement('div');
      msgDiv.className = 'messageContent';
      msgDiv.textContent = `The Project ${pipName} does not have any test data`;
      const br = document.createElement('br');
      messageEl.replaceChildren(br, msgDiv);
      setVisible(messageEl, true);
    }
    if (printBtn) setVisible(printBtn, false);
    return;
  }

  if (printBtn) setVisible(printBtn, true);
  generateHeader(teamName, pipName, tocList);
  generateTable(tdsList, numeratorName, denominatorName, valueName, outsideVarName);
  generateLineChartWithSvg(tdsList, valueName);
  generateBarChartWithSvg(tdsOutVarList, valueName, outsideVarName);
  generateBarChartTable(tdsOutVarList, numeratorName, denominatorName, valueName, outsideVarName);
};

// ─── HTML string builders (for Blob URL page generation) ──────────────────────

const HTML_STYLES = `
body{font-family:Arial,sans-serif;margin:0;padding:20px;background:#f9fafb;}
.page-container{max-width:960px;margin:0 auto;}
h2{color:#1e3a5f;margin:0 0 16px;font-size:18px;}
h3{color:#1e3a5f;margin:0 0 12px;font-size:15px;}
section{background:#fff;border-radius:8px;padding:16px 20px;margin-bottom:20px;box-shadow:0 1px 3px rgba(0,0,0,.12);}
table.data{border-collapse:collapse;width:100%;font-size:12px;}
table.data th,table.data td{border:1px solid #d1d5db;padding:7px 10px;}
table.data th{background:#f3f4f6;font-weight:600;text-align:left;}
td.right{text-align:right;}
.info-table{border-collapse:collapse;margin-bottom:12px;}
.info-table th{font-weight:600;background:#f3f4f6;border:1px solid #d1d5db;padding:6px 10px;font-size:12px;width:140px;}
.info-table td{background:#FCF5D3;border:1px solid #d1d5db;padding:6px 10px;font-size:12px;}
.chart-container{overflow-x:auto;}
svg{overflow:visible;max-width:100%;}
@media print{@page{margin:0;size:auto;}body{padding:10mm;background:#fff;}}
`.trim();

/** Builds a `<th>` HTML string with escaped text. */
const htmlTh = (text: string, width?: string): string => {
  const style = width ? ` style="width:${width}"` : '';
  return `<th class="data"${style}>${escapeHtml(text)}</th>`;
};

/** Builds a `<td>` HTML string with escaped text. */
const htmlTd = (text: string, alignRight = false): string =>
  alignRight
    ? `<td class="data right">${escapeHtml(text)}</td>`
    : `<td class="data">${escapeHtml(text)}</td>`;

/** Builds the project info table section HTML string. */
const buildInfoSectionHtml = (teamName: string, pipName: string): string =>
  `<section>` +
  `<table class="info-table">` +
  `<tr><th>Name Of UBT:</th><td>${escapeHtml(teamName)}</td></tr>` +
  `<tr><th>Name Of Project:</th><td>${escapeHtml(cleanForViewing(pipName))}</td></tr>` +
  `</table></section>`;

/** Builds the Test of Change summary table section HTML string. */
const buildTocSectionHtml = (tocList: TocItem[]): string => {
  if (tocList.length === 0) return '';
  const rows = tocList
    .map((t) => `<tr>${htmlTd(t.testOfChangeTitle)}${htmlTd(t.outcome)}${htmlTd(t.rating + '-' + t.ratingDesc)}</tr>`)
    .join('');
  return (
    `<section>` +
    `<table class="data"><thead><tr>` +
    `${htmlTh('Test Of Change', '40%')}${htmlTh('Outcome', '45%')}${htmlTh('Rating', '15%')}` +
    `</tr></thead><tbody>${rows}</tbody></table></section>`
  );
};

/** Builds the SVG line chart section HTML string. */
const buildLineChartSectionHtml = (tdsList: TdsItem[], valueName: string): string => {
  if (tdsList.length === 0) return '';
  return (
    `<section>` +
    `<div class="chart-container">${generateLineChartSvg(tdsList, valueName)}</div></section>`
  );
};

/** Builds the test data table section HTML string. */
const buildTestDataTableSectionHtml = (
  tdsList: TdsItem[],
  numeratorName: string,
  denominatorName: string,
  valueName: string,
  outsideVarName: string,
): string => {
  if (tdsList.length === 0) return '';
  const cols: [string, string][] = [
    ['Test Of Change', '16%'],
    ['Date Of Test', '14%'],
    ['Shift', '8%'],
    [numeratorName || 'Numerator', '12%'],
    [denominatorName || 'Denominator', '10%'],
    [valueName || 'Value', '10%'],
    ['Notes', '15%'],
    [outsideVarName || 'Other', '15%'],
  ];
  const headerHtml = cols.map(([t, w]) => htmlTh(t, w)).join('');
  const rows = tdsList
    .map(
      (item) =>
        `<tr>` +
        `${htmlTd(truncateText(item.testOfChange, 20))}` +
        `${htmlTd(item.testDate)}` +
        `${htmlTd(item.shiftDesc)}` +
        `${htmlTd(String(item.numeratorValue))}` +
        `${htmlTd(String(item.denominatorValue))}` +
        `${htmlTd(String(formatValueForDisplay(item.value)), true)}` +
        `${htmlTd(item.notes ?? '')}` +
        `${htmlTd(item.outsideVarValue ?? '')}` +
        `</tr>`,
    )
    .join('');
  return (
    `<section>` +
    `<table class="data"><thead><tr>${headerHtml}</tr></thead><tbody>${rows}</tbody></table></section>`
  );
};

/** Builds the SVG bar chart section HTML string. */
const buildBarChartSectionHtml = (
  tdsOutVarList: TdsOutVarItem[],
  valueName: string,
  outsideVarName: string,
): string => {
  if (tdsOutVarList.length === 0) return '';
  return (
    `<section>` +
    `<div class="chart-container">${generateBarChartSvg(tdsOutVarList, valueName, outsideVarName)}</div></section>`
  );
};

/** Builds the outside variable summary table section HTML string. */
const buildOutVarSectionHtml = (
  tdsOutVarList: TdsOutVarItem[],
  numeratorName: string,
  denominatorName: string,
  outsideVarName: string,
): string => {
  if (tdsOutVarList.length === 0) return '';
  const rows = tdsOutVarList
    .map((item) => {
      const avg =
        item.sumDenominator === 0
          ? '0'
          : String(formatValueForDisplay(item.sumNumerator / item.sumDenominator));
      return (
        `<tr>` +
        `${htmlTd(item.outsideVarValue ?? '')}` +
        `${htmlTd(String(item.sumNumerator))}` +
        `${htmlTd(String(item.sumDenominator))}` +
        `${htmlTd(avg, true)}` +
        `</tr>`
      );
    })
    .join('');
  return (
    `<section>` +
    `<table class="data"><thead><tr>` +
    `${htmlTh(outsideVarName || 'Other')}` +
    htmlTh(`Total ${numeratorName || 'Numerator'}`) +
    htmlTh(`Total ${denominatorName || 'Denominator'}`) +
    `${htmlTh('Total Average')}` +
    `</tr></thead><tbody>${rows}</tbody></table></section>`
  );
};

/**
 * Inline JavaScript injected into the Blob HTML page to provide cursor-following
 * tooltips on SVG data points, matching the jQPlot highlighter plugin behaviour.
 * The script attaches mousemove/mouseleave listeners to every <svg> that contains
 * a .chart-tooltip element and positions the tooltip near the cursor.
 */
const TOOLTIP_SCRIPT = [
  '(function(){',
  'function setup(){',
  'document.querySelectorAll("svg").forEach(function(svg){',
  'var tt=svg.querySelector(".chart-tooltip");if(!tt)return;',
  'var tRect=tt.querySelector("rect");var tText=tt.querySelector("text");',
  'svg.addEventListener("mousemove",function(e){',
  'var el=e.target;var tip=el.getAttribute("data-tip");',
  'if(!tip){tt.setAttribute("visibility","hidden");return;}',
  'tText.textContent=tip;',
  'var bb;try{bb=tText.getBBox();}catch(x){return;}',
  'var p=8;',
  'tRect.setAttribute("width",bb.width+p*2);',
  'tRect.setAttribute("height",bb.height+p*2);',
  'tText.setAttribute("x",p);',
  'tText.setAttribute("y",bb.height+p-2);',
  'var pt=svg.createSVGPoint();pt.x=e.clientX;pt.y=e.clientY;',
  'try{var sp=pt.matrixTransform(svg.getScreenCTM().inverse());',
  'tt.setAttribute("transform","translate("+(sp.x+12)+","+(sp.y-36)+")");}catch(x){}',
  'tt.setAttribute("visibility","visible");',
  '});',
  'svg.addEventListener("mouseleave",function(){tt.setAttribute("visibility","hidden");});',
  '});',
  '}',
  'document.readyState==="loading"?document.addEventListener("DOMContentLoaded",setup):setup();',
  '})();',
].join('');

/**
 * Builds a complete, self-contained HTML document string for rendering
 * test data charts via a Blob URL.
 *
 * Uses the same SVG generators and formatting helpers as the DOM rendering
 * path, but produces a serialisable string suitable for `new Blob([html], { type: 'text/html' })`.
 * All user-supplied values are HTML-escaped to prevent XSS.
 *
 * @param chartData - API response payload containing tdsList, tocList, tdsOutVarList.
 * @param params - Display parameters (team name, column labels, etc.).
 * @returns Complete HTML document string.
 */
export const buildHtmlString = (chartData: ChartData, params: ChartParams): string => {
  const { tdsList, tdsOutVarList, tocList } = chartData;
  const { teamName, pipName, numeratorName, denominatorName, valueName, outsideVarName } = params;
  const title = escapeHtml(cleanForViewing(pipName));

  const body =
    tdsList.length === 0
      ? `<section><p>The Project ${title} does not have any test data.</p></section>`
      : buildTocSectionHtml(tocList) +
        buildLineChartSectionHtml(tdsList, valueName) +
        buildTestDataTableSectionHtml(tdsList, numeratorName, denominatorName, valueName, outsideVarName) +
        buildBarChartSectionHtml(tdsOutVarList, valueName, outsideVarName) +
        buildOutVarSectionHtml(tdsOutVarList, numeratorName, denominatorName, outsideVarName);

  return (
    `<!DOCTYPE html><html lang="en"><head>` +
    `<meta charset="UTF-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1.0">` +
    `<title>Test Data Charts - ${title}</title>` +
    `<style>${HTML_STYLES}</style>` +
    `</head><body>` +
    `<div class="page-container">` +
    buildInfoSectionHtml(teamName, pipName) +
    body +
    `</div>` +
    // Split closing tag to prevent browser from treating it as end of an outer script block
    `<script>${TOOLTIP_SCRIPT}</` + `script>` +
    `</body></html>`
  );
};

// ─── API fetch functions ───────────────────────────────────────────────────────

/**
 * Fetches chart data from the API and renders the full page.
 * Replaces the legacy `loadChartDataNoDialog` jQuery AJAX call.
 *
 * @param teamName - Display name of the UBT team.
 * @param perfId - Project performance ID used as the API query parameter.
 * @param pipName - Project name for display.
 * @param numeratorName - Label for the numerator column.
 * @param denominatorName - Label for the denominator column.
 * @param valueName - Label for the calculated value column.
 * @param outsideVarName - Label for the outside variable column.
 */
export const loadChartDataNoDialog = async (
  teamName: string,
  perfId: string,
  pipName: string,
  numeratorName: string,
  denominatorName: string,
  valueName: string,
  outsideVarName: string,
): Promise<void> => {
  const params: ChartParams = { teamName, pipName, numeratorName, denominatorName, valueName, outsideVarName };
  try {
    const response = await api.get<ChartApiResponse>(
      endpoints.ubt.teamsReporting.teamsReportingList,
      { params: { perfId } },
    );
    buildPage(response.data.data, params);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error fetching chart data.';
    const messageEl = getElementById('message');
    if (messageEl) {
      messageEl.textContent = `Error loading chart data: ${message}`;
      setVisible(messageEl, true);
    }
  }
};

/**
 * Fetches chart data from the API and renders the full page.
 * Alias for `loadChartDataNoDialog`; replaces the legacy `loadChartData` function.
 */
export const loadChartData = loadChartDataNoDialog;
